/**
 * « Ta voix » (lot 6) : enregistrer la narration de chaque scène de
 * l'animation, dans l'écran « Tester ».
 *
 * Une ligne par voix possible (accroche et fin de la vidéo, intro, chaque
 * scène) : « Voix du navigateur » ou l'avatar et le prénom de qui l'a
 * enregistrée, avec sa durée (« Philippe (toi) · 12,4 s »). « Enregistrer »
 * ouvre le PROMPTEUR (le texte à dire, en grand), le micro et son niveau ;
 * « Annuler la prise » la jette ; « Arrêter » l'envoie EN ARRIÈRE-PLAN à
 * `animation-voice` (Whisper : ce qui a été entendu, mots horodatés), qui la
 * dépose, pendant qu'on enregistre la scène suivante (`voice-takes.tsx`) ;
 * on la réécoute ensuite, on voit ce qui a été entendu et si ça colle au
 * texte, puis « Garder » la pose dans le brouillon (silences du début et de la fin rognés à la lecture). Un
 * fichier peut aussi être importé (une autre voix). Les élèves l'entendent
 * après « Republier ».
 */

import { useId, useMemo, useState } from 'react';
import { Mic, Pencil, Trash2 } from 'lucide-react';
import {
  JRAF_PALETTE,
  voiceMatch,
  voiceSlots,
  VOICE_MATCH_MIN,
  withVoice,
  withVoiceSpeaker,
  type JrafDocument,
} from '@jeraisonne/animation';
import { AvatarCircle, Button } from '@jeraisonne/ui';
import { RecordingBar, TakeCard, useVoiceTakes } from '@/features/animations/voice-takes';
import { checkedDraft, useSaveAnimationDraft, type AnimationRow } from '@/lib/animations/api';
import { useVoiceSpeakerProfiles, type VoiceSpeakerProfiles } from '@/lib/animations/speakers';
import { animationAssetUrl } from '@/lib/animations/voice';
import { useAuth } from '@/lib/auth';
import { isEditableTarget } from '@/lib/animations/narration-edit';
import { NarrationTextEditor } from '@/features/animations/narration-text-editor';
import { firstName } from '@/lib/first-name';

const HORA_BG: Record<string, string> = {
  H: JRAF_PALETTE['hora-h'],
  O: JRAF_PALETTE['hora-o'],
  R: JRAF_PALETTE['hora-r'],
  A: JRAF_PALETTE['hora-a'],
};
const HORA_FG: Record<string, string> = {
  H: JRAF_PALETTE.paper,
  O: JRAF_PALETTE.night,
  R: JRAF_PALETTE.night,
  A: JRAF_PALETTE.paper,
};

/** « 12,4 s » */
export function seconds(ms: number): string {
  return `${(ms / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}\u202fs`;
}

/** Qui a enregistré une voix : prénom posé sur la voix, sinon celui du compte. */
export function voiceSpeakerOf(
  voice: { speaker?: string; speakerId?: string },
  profiles: VoiceSpeakerProfiles,
  viewerId: string | null | undefined,
): { name: string; avatarUrl: string | null; isMe: boolean } {
  const profile = voice.speakerId ? profiles[voice.speakerId] : undefined;
  const name =
    voice.speaker?.trim() || firstName(profile?.displayName ?? null, '') || 'Voix enregistrée';
  return {
    name,
    avatarUrl: profile?.avatarUrl ?? null,
    isMe: !!viewerId && voice.speakerId === viewerId,
  };
}

export function AnimationVoicePanel({
  row,
  doc,
  toolIds,
  dropped = [],
}: {
  row: Pick<AnimationRow, 'id' | 'draft'>;
  doc: JrafDocument;
  toolIds?: readonly string[];
  /** voix retirées par la dernière correction de l'IA (texte changé) */
  dropped?: readonly string[];
}) {
  const slots = useMemo(() => voiceSlots(doc), [doc]);
  const assets = useMemo(() => new Map(doc.assets.map((a) => [a.id, a])), [doc]);
  const recorded = slots.filter((s) => s.audioAssetId).length;
  const { profile, user } = useAuth();
  // ton prénom : celui de TES voix (une animation peut être enregistrée à plusieurs)
  const savedSpeaker = doc.assets.flatMap((a) =>
    a.kind === 'audio' && a.speaker && (!a.speakerId || a.speakerId === user?.id)
      ? [a.speaker]
      : [],
  )[0];
  const [speaker, setSpeaker] = useState(
    savedSpeaker ?? firstName(profile?.display_name ?? null, ''),
  );
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const save = useSaveAnimationDraft();
  const takes = useVoiceTakes(row.id);
  const [keeping, setKeeping] = useState<string | null>(null);
  const speakerId = useId();
  const speakerIds = doc.assets.flatMap((a) =>
    a.kind === 'audio' && a.speakerId ? [a.speakerId] : [],
  );
  const profiles = useVoiceSpeakerProfiles(speakerIds);

  const commit = (draft: unknown, after?: () => void) =>
    save.mutate({ id: row.id, ...checkedDraft(draft, toolIds) }, { onSuccess: () => after?.() });

  return (
    <div className="space-y-3">
      <p className="text-ink-600 text-[13px]">
        <span className="text-ink-900 font-semibold">
          {recorded} / {slots.length} avec ta voix
        </span>{' '}
        — ailleurs, la voix du navigateur. Les élèves l’entendent après « Republier ».
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={speakerId} className="text-ink-700 text-[13px] font-semibold">
          Qui parle
        </label>
        <input
          id={speakerId}
          value={speaker}
          maxLength={60}
          onChange={(e) => setSpeaker(e.target.value)}
          onBlur={() => {
            if (recorded > 0 && speaker.trim() !== (savedSpeaker ?? ''))
              commit(withVoiceSpeaker(row.draft, speaker, user?.id));
          }}
          placeholder="Ton prénom"
          className="border-ink-200 focus:border-primary-400 min-h-11 w-44 rounded-xl border bg-white px-3 text-sm outline-none"
        />
        <span className="text-ink-500 text-[12px]">
          générique : « Voix : {speaker.trim() || '…'} »
        </span>
      </div>

      <ul className="divide-ink-100 border-ink-200 divide-y overflow-hidden rounded-2xl border bg-white">
        {slots.map((slot) => {
          const asset = slot.audioAssetId ? assets.get(slot.audioAssetId) : undefined;
          const voice = asset?.kind === 'audio' ? asset : undefined;
          const match = voice ? voiceMatch(slot, voice) : null;
          const who = voice ? voiceSpeakerOf(voice, profiles, user?.id) : null;
          return (
            <li key={slot.key} className="p-3 sm:p-4" data-slot={slot.key}>
              <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 flex flex-wrap items-center gap-1.5 text-sm font-semibold">
                    {slot.hora && (
                      <span
                        className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold"
                        style={{ backgroundColor: HORA_BG[slot.hora], color: HORA_FG[slot.hora] }}
                        aria-hidden
                      >
                        {slot.hora}
                      </span>
                    )}
                    <span>{slot.label}</span>
                    {slot.videoOnly && (
                      <span className="bg-ink-100 text-ink-600 rounded-full px-2 py-0.5 text-[11px] font-semibold">
                        vidéo
                      </span>
                    )}
                    {slot.interactive && (
                      <span className="bg-ink-100 text-ink-600 rounded-full px-2 py-0.5 text-[11px] font-semibold">
                        avec accès
                      </span>
                    )}
                  </p>
                  <p className="text-ink-500 mt-0.5 line-clamp-2 text-[13px]">{slot.say}</p>
                </div>
                {voice && who ? (
                  <span
                    className="bg-success-50 text-success-600 inline-flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2.5 text-[12px] font-semibold"
                    data-testid="voice-speaker"
                  >
                    <AvatarCircle name={who.name} src={who.avatarUrl} size="xs" />
                    <span className="text-ink-900">
                      {who.name}
                      {who.isMe ? ' (toi)' : ''}
                    </span>
                    <span>· {seconds(voice.durationMs)}</span>
                  </span>
                ) : (
                  <span className="bg-ink-100 text-ink-600 rounded-full px-2.5 py-1 text-[12px] font-semibold">
                    Voix du navigateur
                  </span>
                )}
              </div>

              {!voice && dropped.includes(slot.key) && (
                <p className="border-warning-500/40 bg-warning-50 text-ink-800 mt-2 rounded-lg border px-2.5 py-1.5 text-[13px] font-semibold">
                  La correction de l’IA a changé ce texte : ta voix a été retirée, réenregistre-la.
                </p>
              )}

              {voice && match !== null && match < VOICE_MATCH_MIN && (
                <p className="border-warning-500/40 bg-warning-50 text-ink-800 mt-2 rounded-lg border px-2.5 py-1.5 text-[13px] font-semibold">
                  Cette prise ne dit plus le texte ({Math.round(match * 100)} % des mots) :
                  réenregistre-la.
                </p>
              )}

              {open !== slot.key && voice && (
                <audio
                  controls
                  preload="metadata"
                  src={animationAssetUrl(voice.path)}
                  aria-label={
                    who?.isMe
                      ? `Écouter ta voix : ${slot.label}`
                      : voice.speaker?.trim() || voice.speakerId
                        ? `Écouter la voix de ${who?.name} : ${slot.label}`
                        : `Écouter la voix : ${slot.label}`
                  }
                  className="mt-2 h-10 w-full sm:max-w-md"
                />
              )}
              {editing === slot.key && (
                <NarrationTextEditor
                  draft={row.draft}
                  doc={doc}
                  slot={slot}
                  toolIds={toolIds}
                  saving={save.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(checked) =>
                    save.mutate({ id: row.id, ...checked }, { onSuccess: () => setEditing(null) })
                  }
                />
              )}

              {open !== slot.key && editing !== slot.key && !takes.takes[slot.key] && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant={voice ? 'outline' : 'primary'}
                    onClick={() => setOpen(slot.key)}
                  >
                    <Mic className="h-4 w-4" aria-hidden />
                    {voice ? 'Refaire' : 'Enregistrer'}
                  </Button>
                  {isEditableTarget(slot.target) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(slot.key)}
                      aria-label={`Modifier le texte : ${slot.label}`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden /> Modifier le texte
                    </Button>
                  )}
                  {voice && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={save.isPending}
                      onClick={() => commit(withVoice(row.draft, slot.target, null))}
                      aria-label={`Retirer ta voix : ${slot.label}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden /> Retirer
                    </Button>
                  )}
                </div>
              )}

              {open === slot.key && (
                <RecordingBar
                  slot={slot}
                  onClose={() => setOpen(null)}
                  onDone={(blob, name) => {
                    // analysée en arrière-plan : on peut enregistrer la scène suivante
                    takes.submit(slot, blob, name);
                    setOpen(null);
                  }}
                />
              )}
              {open !== slot.key && takes.takes[slot.key] && (
                <TakeCard
                  state={takes.takes[slot.key]!}
                  saving={save.isPending && keeping === slot.key}
                  onRedo={() => setOpen(slot.key)}
                  onDiscard={() => takes.discard(slot.key)}
                  onForce={() => takes.force(slot.key)}
                  onKeep={(take) => {
                    setKeeping(slot.key);
                    commit(
                      withVoice(row.draft, slot.target, {
                        path: take.path,
                        durationMs: take.durationMs,
                        words: take.words,
                        speaker,
                        // compte qui garde la prise : son avatar animé (0675)
                        speakerId: profile?.id,
                      }),
                      () => takes.discard(slot.key),
                    );
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
      {save.error && (
        <p role="alert" className="text-error-600 text-sm">
          {save.error.message}
        </p>
      )}
    </div>
  );
}
